"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

interface ValueRow {
  id: number;
  value: string;
  weight: string;
}

let nextRowId = 0;

function makeRow(value: string, weight: string): ValueRow {
  nextRowId += 1;
  return { id: nextRowId, value, weight };
}

interface WeightedResult {
  mean: number;
  weightSum: number;
  productSum: number;
  pairs: { value: number; weight: number }[];
}

function computeWeightedMean(rows: ValueRow[]): WeightedResult | null {
  const pairs: { value: number; weight: number }[] = [];
  for (const row of rows) {
    const value = parseNumberInput(row.value);
    const weight = parseNumberInput(row.weight);
    if (value !== null && weight !== null && weight > 0) {
      pairs.push({ value, weight });
    }
  }
  if (pairs.length === 0) {
    return null;
  }
  let weightSum = 0;
  let productSum = 0;
  for (const pair of pairs) {
    weightSum += pair.weight;
    productSum += pair.weight * pair.value;
  }
  return { mean: productSum / weightSum, weightSum, productSum, pairs };
}

/**
 * A value or a weight, written the reader's way.
 *
 * `(Math.round(n * 10_000) / 10_000).toString()` rounded to four places and then handed
 * the result to a `toString` that always writes a period, so the German page showed
 * `2.5 × 90` under a weighted average of `86,25`. The formatter is threaded in because
 * this is module level; the working lines join with ` + ` and ` × `, never a comma, so a
 * comma decimal reads unambiguously.
 */
function fmt(n: number, format: NumberFormatter): string {
  return format(n, { maximumFractionDigits: 4 });
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const WEIGHTED_AVERAGE_CALCULATOR_STRINGS = {
  valuesAndWeights: "Values and Weights",
  row: "Row",
  value: "Value",
  weight: "Weight",
  remove: "Remove",
  removeAriaLabel: "Remove row {n}",
  addRow: "Add Row",
  weightedAverage: "Weighted average",
  sumOfWeights: "Sum of weights",
  rowsCounted: "Rows counted",
  workingStepByStep: "The working, step by step",
  emptyState:
    "Enter at least one value with a weight greater than zero to see the weighted average.",
} as const;

export default function WeightedAverageCalculatorWidget() {
  const strings = useToolUi(WEIGHTED_AVERAGE_CALCULATOR_STRINGS);
  const formatNum = useToolNumber();
  const [rows, setRows] = useState<ValueRow[]>(() => [
    makeRow("92", "20"),
    makeRow("85", "40"),
    makeRow("", ""),
  ]);

  const result = computeWeightedMean(rows);

  function updateRow(id: number, patch: Partial<ValueRow>) {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    setRows((prev) => [...prev, makeRow("", "")]);
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }

  return (
    <>
      {/* Value rows */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.valuesAndWeights}
        </h2>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"
              key={row.id}
            >
              <p className="w-16 font-medium text-muted-foreground text-sm">
                {strings.row} {index + 1}
              </p>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`wa-value-${row.id}`}
                >
                  {strings.value}
                </label>
                <input
                  className="w-28 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`wa-value-${row.id}`}
                  onChange={(e) => updateRow(row.id, { value: e.target.value })}
                  step="any"
                  type="number"
                  value={row.value}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`wa-weight-${row.id}`}
                >
                  {strings.weight}
                </label>
                <input
                  className="w-28 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`wa-weight-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { weight: e.target.value })
                  }
                  step="any"
                  type="number"
                  value={row.weight}
                />
              </div>
              <button
                aria-label={strings.removeAriaLabel.replace(
                  "{n}",
                  String(index + 1)
                )}
                className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={rows.length <= 1}
                onClick={() => removeRow(row.id)}
                type="button"
              >
                {strings.remove}
              </button>
            </div>
          ))}
        </div>
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={addRow}
          type="button"
        >
          {strings.addRow}
        </button>
      </section>

      {/* Result */}
      {result && (
        <section className="space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.weightedAverage}
              value={fmt(result.mean, formatNum)}
            />
            <StatCard
              label={strings.sumOfWeights}
              value={fmt(result.weightSum, formatNum)}
            />
            <StatCard label={strings.rowsCounted} value={result.pairs.length} />
          </div>
          <div className="space-y-2 rounded-lg border border-border bg-card p-4">
            <p className="font-medium text-foreground text-sm">
              {strings.workingStepByStep}
            </p>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              Σ(w × x) ={" "}
              {result.pairs
                .map(
                  (p) =>
                    `${fmt(p.weight, formatNum)} × ${fmt(p.value, formatNum)}`
                )
                .join(" + ")}{" "}
              ={" "}
              {result.pairs
                .map((p) => fmt(p.weight * p.value, formatNum))
                .join(" + ")}{" "}
              = {fmt(result.productSum, formatNum)}
            </p>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              Σw ={" "}
              {result.pairs.map((p) => fmt(p.weight, formatNum)).join(" + ")} ={" "}
              {fmt(result.weightSum, formatNum)}
            </p>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              weighted average = {fmt(result.productSum, formatNum)} ÷{" "}
              {fmt(result.weightSum, formatNum)} = {fmt(result.mean, formatNum)}
            </p>
          </div>
        </section>
      )}
      {!result && (
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm leading-relaxed">
          {strings.emptyState}
        </p>
      )}
    </>
  );
}
